import React, { useEffect, useRef } from 'react'
import { Animated, Pressable, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native'
import { DS } from '@/design-system'

type ToggleRowProps = {
  label: string
  value: boolean
  onValueChange: (value: boolean) => void
  disabled?: boolean
  style?: StyleProp<ViewStyle>
}

const TRACK_WIDTH = 42
const TRACK_HEIGHT = 24
const THUMB_SIZE = 20
const THUMB_OFFSET_OFF = 2
const THUMB_OFFSET_ON = TRACK_WIDTH - THUMB_SIZE - THUMB_OFFSET_OFF

function Toggle({ value, onValueChange, disabled }: Omit<ToggleRowProps, 'label'>) {
  const anim = useRef(new Animated.Value(value ? 1 : 0)).current

  useEffect(() => {
    Animated.spring(anim, {
      toValue: value ? 1 : 0,
      useNativeDriver: false,
      friction: 8,
      tension: 80
    }).start()
  }, [value, anim])

  const translateX = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [THUMB_OFFSET_OFF, THUMB_OFFSET_ON]
  })

  const backgroundColor = anim.interpolate({
    inputRange: [0, 1],
    outputRange: [DS.palette['Brand/Light Grey'], DS.palette['Brand/Maroon']]
  })

  return (
    <Pressable
      onPress={() => {
        if (!disabled) onValueChange(!value)
      }}
      accessibilityRole="switch"
      accessibilityState={{ checked: value, disabled }}
      style={{ opacity: disabled ? 0.4 : 1 }}
    >
      <Animated.View style={[styles.track, { backgroundColor }]}>
        <Animated.View style={[styles.thumb, { transform: [{ translateX }] }]} />
      </Animated.View>
    </Pressable>
  )
}

export function ToggleRow({ label, value, onValueChange, disabled, style }: ToggleRowProps) {
  return (
    <View style={[styles.row, style]}>
      <DS.Text variant="Body Large/Regular" style={{ flex: 1 }}>
        {label}
      </DS.Text>
      <Toggle value={value} onValueChange={onValueChange} disabled={disabled} />
    </View>
  )
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    gap: 12
  },
  track: {
    width: TRACK_WIDTH,
    height: TRACK_HEIGHT,
    borderRadius: TRACK_HEIGHT / 2,
    justifyContent: 'center'
  },
  thumb: {
    width: THUMB_SIZE,
    height: THUMB_SIZE,
    borderRadius: THUMB_SIZE / 2,
    backgroundColor: DS.palette['Global/White'],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.15,
    shadowRadius: 2,
    elevation: 2
  }
})
